import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import React, { useEffect, useRef, useState, useCallback } from "react";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TpStudioLoader from "../../client/tp-studio/tp-studio-loader";
import "../../../assets/topstudio/styles/Player.css";
import leftSceneSvg from "../../../assets/chicken-left-background.svg";
import chickenSpriteSvg from "../../../assets/chicken-sprite.svg";
import manholeSvg from "../../../assets/chicken-manhole.svg";
import manholeReachedSvg from "../../../assets/mainhole-yellow.svg";
import barrierSvg from "../../../assets/barrier.svg";
import axiosInstance from "../../../utils/axiosInstance";
import { useDispatch } from "react-redux";
import { setUserData } from "../../../store/userSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";




const LANE_MULTIPLIERS = [
    1.01, 1.03, 1.06, 1.10, 1.15, 1.19, 1.24, 1.30, 1.37, 1.45,
    1.55, 1.67, 1.81, 1.98, 2.18, 2.42, 2.71, 3.06, 3.49, 4.01,
    4.65, 5.45, 6.50, 7.80, 9.50, 11.80, 14.50, 18.00, 20.50, 23.24,
];

const CHICKEN_COLORS = {
    body: "#fff8d6",
    bodyShade: "#f1d77a",
    beak: "#ff9b21",
    comb: "#e63946",
    leg: "#ff9b21",
    eye: "#1a1a1a",
};

const CAR_COLORS = ["#ff3b3b", "#36a2ff", "#ffcd2b", "#22c25b", "#a259ff", "#ff7a59"];
const CRASH_RESET_MS = 5000;
const WIN_HOLD_MS = 5000;
const HOP_DURATION = 0.2;

function CrashShieldIcon() {
    return (
        <svg
            className="cr-crash-toast-icon-svg"
            viewBox="0 0 40 44"
            width="34"
            height="38"
            aria-hidden="true"
        >
            <path
                d="M20 2 L36 10 V22 C36 32 28 40 20 42 C12 40 4 32 4 22 V10 Z"
                fill="#ffffff"
            />
            <circle cx="20" cy="22" r="8.5" fill="#e53935" />
            <path
                d="M20 16.5 V24.5 M20 27.5 V28.5"
                stroke="#ffffff"
                strokeWidth="2.6"
                strokeLinecap="round"
            />
        </svg>
    );
}

function showCrashToast() {
    toast(
        <div className="cr-crash-toast-body">
            <span className="cr-crash-toast-icon">
                <CrashShieldIcon />
            </span>
            <span className="cr-crash-toast-text">You got hit! 😭</span>
        </div>,
        {
            className: "cr-crash-toast",
            icon: false,
            closeButton: false,
            autoClose: 2800,
            hideProgressBar: false,
            position: "top-center",
        }
    );
}

function getDiscMetrics(world) {
    const discY = (world.roadTop + world.roadBottom) / 2 + 30;
    const isMobile = world.width < 600;
    const maxR = isMobile ? 110 : 96;
    const discR = Math.min(maxR, world.laneWidth * 0.42);
    return { discY, discR };
}

function reservedAmbientLanes(s) {
    const reserved = new Set();
    if (!s || !s.world) return reserved;
    const laneCount = s.world.laneCount || 0;

    const add = (lane0) => {
        if (lane0 >= 0 && lane0 < laneCount) reserved.add(lane0);
    };

    if (s.phase === "idle") {
        add(0);
        return reserved;
    }

    const current0 = s.laneIndex - 1;
    add(current0);
    add(s.laneIndex);
    if (s.phase === "playing" && s.pendingLane != null) {
        add(s.pendingLane - 1);
    }
    return reserved;
}

function hasLaneBarrier(s, laneIdx) {
    if (laneIdx < 0 || laneIdx >= s.world.laneCount) return false;
    if (s.pendingCrash || s.phase === "crashed") return false;

    if (s.winBarriersActive) {
        return laneIdx < s.winBarrierLaneIndex;
    }

    if (s.phase === "idle" || s.laneIndex <= 0) return false;

    const crossed = Number.isFinite(s.safeLaneIndex) ? s.safeLaneIndex : s.laneIndex;
    return laneIdx < crossed;
}

function getBarrierLayout(s) {
    const { discY, discR } = getDiscMetrics(s.world);
    const barrierW = s.world.laneWidth * 0.78 * 1.2 * 1.1;
    const barrierH = barrierW * (132 / 300);
    const centerY = discY - discR - barrierH * 0.45 - 8;
    return { centerY, barrierW, barrierH };
}

function laneIdxFromCarX(s, x) {
    const rel = x - s.world.leftSceneW;
    const idx = Math.floor(rel / s.world.laneWidth + 1e-6);
    if (idx < 0 || idx >= s.world.laneCount) return -1;
    const cx = s.world.leftSceneW + s.world.laneWidth * (idx + 0.5);
    if (Math.abs(x - cx) > s.world.laneWidth * 0.6) return -1;
    return idx;
}

export default function ChickenRoad() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [displayLoader, setDisplayLoader] = useState(true);

  // Refresh balance / exposure shown in the top-right header.
  const refreshUserData = async () => {
    try {
      const res = await axiosInstance.get("/user", { withCredentials: true });
      if (res && res.status === 200 && res.data) {
        dispatch(setUserData(res.data));
      }
    } catch (err) {
      console.error("refresh /user failed:", err);
    }
  };

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(0);

  const [phase, setPhase] = useState("idle");
  const [laneIndex, setLaneIndex] = useState(0);
  const [multiplier, setMultiplier] = useState(1.0);
  const [betAmount, setBetAmount] = useState(2000);
  const [betLoading, setBetLoading] = useState(false);
  const [hopBusy, setHopBusy] = useState(false);
  const [roundId, setRoundId] = useState("");
  const roundRef = useRef(null); // { cat_mid, multipliers }
  const crashResetRef = useRef(null);
  const cashResetRef = useRef(null);
  const hopInFlightRef = useRef(false);
  const requestHopRef = useRef(null);
  const playCashoutRef = useRef(null);
  const spawnKillCarRef = useRef(null);
  const applyOpenRoundRef = useRef(null);

  /* ---- init mutable game state once ---- */
  useEffect(() => {
    stateRef.current = {
      phase: "idle",
      laneIndex: 0,
      multiplier: 1.0,
      pendingLane: null,
      pendingCrash: false,
      pendingMultiplier: null,
      chicken: {
        x: 0,
        y: 0,
        hopFromX: 0,
        hopFromY: 0,
        targetX: 0,
        targetY: 0,
        hopT: 0,
        hopDuration: 0,
        hopArc: 0,
        walkT: 0,
        facing: 1
      },
      cars: [],
      carSpawnTimer: 0,
      shake: 0,
      crashFlash: 0,
      cashFlash: 0,
      timePrev: 0,
      world: {
        width: 0,
        height: 0,
        laneCount: LANE_MULTIPLIERS.length,
        laneWidth: 110,
        roadTop: 0,
        roadBottom: 0,
        sidewalkW: 90,
        leftSceneW: 90,
        offsetX: 0
      },
      confetti: [],
      feathers: [],
      leftSceneImg: null,
      chickenImg: null,
      manholeImg: null,
      manholeReachedImg: null,
      barrierImg: null,
      crashBarriersActive: false,
      crashBarrierLaneIndex: 0,
      safeLaneIndex: 0,
      winLaneIndex: -1,
      winBarriersActive: false,
      winBarrierLaneIndex: 0
    };
    // load left-side scene SVG (chicken + lamp + trees + background)
    const img = new Image();
    img.onload = () => {
      if (stateRef.current) {
        stateRef.current.leftSceneImg = img;
        // trigger a resize-style recompute now that the image dimensions are known
        window.dispatchEvent(new Event("resize"));
      }
    };
    img.src = leftSceneSvg;
    // load chicken sprite SVG
    const chickImg = new Image();
    chickImg.onload = () => {
      if (stateRef.current) stateRef.current.chickenImg = chickImg;
    };
    chickImg.src = chickenSpriteSvg;
    // load manhole disc SVG
    const mhImg = new Image();
    mhImg.onload = () => {
      if (stateRef.current) stateRef.current.manholeImg = mhImg;
    };
    mhImg.src = manholeSvg;
    // load reached/current manhole (golden roast chicken)
    const mhReachedImg = new Image();
    mhReachedImg.onload = () => {
      if (stateRef.current) stateRef.current.manholeReachedImg = mhReachedImg;
    };
    mhReachedImg.src = manholeReachedSvg;
    const barrierImg = new Image();
    barrierImg.onload = () => {
      if (stateRef.current) stateRef.current.barrierImg = barrierImg;
    };
    barrierImg.src = barrierSvg;
    setDisplayLoader(false);
  }, []);

  /* ---- canvas resize / DPR ---- */
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = container.clientWidth;
      const h = Math.max(
        336,
        Math.min(624, Math.round(window.innerHeight * 0.66))
      );
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const s = stateRef.current;
      if (s) {
        s.world.width = w;
        s.world.height = h;
        // size the left scene to match the SVG's aspect ratio at full canvas height,
        // capped so the road still has room.
        const img = s.leftSceneImg;
        let leftSceneW = s.world.sidewalkW;
        if (img && img.naturalWidth > 0) {
          const aspect = img.naturalWidth / img.naturalHeight;
          leftSceneW = Math.min(Math.round(h * aspect), Math.floor(w * 0.55));
        }
        s.world.leftSceneW = leftSceneW;
        // Number of lanes visible at once (the rest scroll into view).
        // Mobile shows ~2.4 lanes so each disc is large and readable.
        const VISIBLE_LANES = w < 600 ? 1.6 : 6;
        const usable = Math.max(160, w - leftSceneW - s.world.sidewalkW);
        const minLaneW = w < 600 ? 56 : 110;
        s.world.laneWidth = Math.max(
          minLaneW,
          Math.floor(usable / VISIBLE_LANES)
        );
        s.world.roadTop = Math.floor(h * 0.2);
        s.world.roadBottom = Math.floor(h * 0.82);
        if (s.phase === "idle" && !s.crashBarriersActive) {
          // sit on the sidewalk just inside the right edge of the left scene (shifted 20% left)
          s.chicken.x = s.world.leftSceneW * 0.8 - 60;
          s.chicken.y = (s.world.roadTop + s.world.roadBottom) / 2;
          s.chicken.targetX = s.chicken.x;
          s.chicken.targetY = s.chicken.y;
        } else if (
          s.phase === "playing" &&
          s.pendingLane == null &&
          (s.chicken.hopDuration <= 0 ||
            s.chicken.hopT >= s.chicken.hopDuration)
        ) {
          if (s.laneIndex < 1) {
            s.chicken.x = s.world.leftSceneW * 0.8 - 60;
            s.chicken.y = (s.world.roadTop + s.world.roadBottom) / 2;
          } else {
            s.chicken.x =
              s.world.leftSceneW + s.world.laneWidth * (s.laneIndex - 0.5);
            s.chicken.y =
              (s.world.roadTop + s.world.roadBottom) / 2 +
              Math.sin(s.laneIndex * 1.7) * 12;
          }
          s.chicken.targetX = s.chicken.x;
          s.chicken.targetY = s.chicken.y;
          s.chicken.hopFromX = s.chicken.x;
          s.chicken.hopFromY = s.chicken.y;
        }
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    window.addEventListener("resize", resize);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(
    () => () => {
      if (crashResetRef.current) clearTimeout(crashResetRef.current);
      if (cashResetRef.current) clearTimeout(cashResetRef.current);
    },
    []
  );

  /* ---- main render loop ---- */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const tick = (t) => {
      const s = stateRef.current;
      if (s) {
        const dt = Math.min(0.05, (t - (s.timePrev || t)) / 1000);
        s.timePrev = t;
        update(s, dt);
        draw(ctx, s, t / 1000);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  /* ============== game control ============== */

  const laneCenterX = (s, idx) =>
    s.world.leftSceneW + s.world.laneWidth * (idx + 0.5);

  const clearCrashResetTimer = () => {
    if (crashResetRef.current) {
      clearTimeout(crashResetRef.current);
      crashResetRef.current = null;
    }
  };

  const clearCashResetTimer = () => {
    if (cashResetRef.current) {
      clearTimeout(cashResetRef.current);
      cashResetRef.current = null;
    }
  };

  const resetChickenToStart = (s) => {
    if (!s) return;
    clearCashResetTimer();
    s.phase = "idle";
    s.laneIndex = 0;
    s.multiplier = 1.0;
    s.pendingLane = null;
    s.pendingCrash = false;
    s.pendingMultiplier = null;
    s.cars = [];
    s.feathers = [];
    s.confetti = [];
    s.crashFlash = 0;
    s.cashFlash = 0;
    s.shake = 0;
    s.crashBarriersActive = false;
    s.crashBarrierLaneIndex = 0;
    s.safeLaneIndex = 0;
    s.winLaneIndex = -1;
    s.winBarriersActive = false;
    s.winBarrierLaneIndex = 0;
    s.world.offsetX = 0;
    s.chicken.x = s.world.leftSceneW * 0.8 - 60;
    s.chicken.y = (s.world.roadTop + s.world.roadBottom) / 2;
    s.chicken.targetX = s.chicken.x;
    s.chicken.targetY = s.chicken.y;
    s.chicken.hopFromX = s.chicken.x;
    s.chicken.hopFromY = s.chicken.y;
    s.chicken.hopT = 0;
    s.chicken.hopDuration = 0;
    s.chicken.hopArc = 0;
    s.chicken.facing = 1;
    setPhase("idle");
    setLaneIndex(0);
    setMultiplier(1.0);
    setHopBusy(false);
    hopInFlightRef.current = false;
  };

  const startGame = useCallback(() => {
    clearCrashResetTimer();
    clearCashResetTimer();
    const s = stateRef.current;
    if (!s || s.phase === "playing") return;
    s.phase = "playing";
    s.laneIndex = 0;
    s.multiplier = 1.0;
    s.cars = [];
    s.feathers = [];
    s.confetti = [];
    s.crashFlash = 0;
    s.cashFlash = 0;
    s.shake = 0;
    s.pendingLane = null;
    s.pendingCrash = false;
    s.pendingMultiplier = null;
    s.crashBarriersActive = false;
    s.crashBarrierLaneIndex = 0;
    s.safeLaneIndex = 0;
    s.winBarriersActive = false;
    s.winBarrierLaneIndex = 0;
    // start hop FROM the sidewalk position (right edge of left scene)
    s.chicken.x = s.world.leftSceneW - 60;
    s.chicken.y = (s.world.roadTop + s.world.roadBottom) / 2;
    s.chicken.targetX = s.chicken.x;
    s.chicken.targetY = s.chicken.y;
    s.chicken.hopT = 0;
    s.chicken.hopDuration = 0;
    s.chicken.facing = 1;
    s.world.offsetX = 0;
    setPhase("playing");
    setLaneIndex(0);
    setMultiplier(1.0);
    hopInFlightRef.current = false;
    setHopBusy(true);
    requestHopRef.current?.();
  }, []);

  const hopHasLanded = (s) => {
    if (!s) return false;
    return s.pendingLane == null && s.chicken.hopT >= s.chicken.hopDuration;
  };

  const clearHopBusyIfIdle = (s) => {
    if (hopInFlightRef.current) return;
    if (!hopHasLanded(s)) return;
    setHopBusy(false);
  };

  const startHopToLane = (s, laneIndex, multiplier) => {
    const next = Number(laneIndex);
    if (!s || !Number.isFinite(next) || next < 1) return;
    const targetX = laneCenterX(s, next - 1);
    const targetY =
      (s.world.roadTop + s.world.roadBottom) / 2 + Math.sin(next * 1.7) * 12;
    s.chicken.hopFromX = s.chicken.x;
    s.chicken.hopFromY = s.chicken.y;
    s.chicken.targetX = targetX;
    s.chicken.targetY = targetY;
    s.chicken.hopT = 0;
    s.chicken.hopDuration = HOP_DURATION;
    s.chicken.hopArc = 36;
    s.chicken.facing = 1;
    s.pendingLane = next;
    s.pendingMultiplier = Number(multiplier);
  };

  const snapToSidewalk = (s) => {
    if (!s) return;
    s.laneIndex = 0;
    s.multiplier = 1.0;
    s.pendingLane = null;
    s.pendingCrash = false;
    s.pendingMultiplier = null;
    s.chicken.x = s.world.leftSceneW - 60;
    s.chicken.y = (s.world.roadTop + s.world.roadBottom) / 2;
    s.chicken.targetX = s.chicken.x;
    s.chicken.targetY = s.chicken.y;
    s.chicken.hopFromX = s.chicken.x;
    s.chicken.hopFromY = s.chicken.y;
    s.chicken.hopT = 0;
    s.chicken.hopDuration = 0;
    s.chicken.hopArc = 0;
    setLaneIndex(0);
    setMultiplier(1.0);
  };

  const placeChickenOnLane = (s, lane, mult) => {
    const next = Number(lane) || 0;
    const shown =
      Number(mult) > 0
        ? Number(mult)
        : next >= 1
        ? LANE_MULTIPLIERS[next - 1] || 1
        : 1.0;
    s.phase = "playing";
    s.laneIndex = next;
    s.multiplier = shown;
    s.pendingLane = null;
    s.pendingCrash = false;
    s.pendingMultiplier = null;
    s.cars = [];
    s.feathers = [];
    s.confetti = [];
    s.crashFlash = 0;
    s.cashFlash = 0;
    s.shake = 0;
    s.crashBarriersActive = false;
    s.crashBarrierLaneIndex = 0;
    s.safeLaneIndex = next;
    s.winLaneIndex = -1;
    s.winBarriersActive = false;
    s.winBarrierLaneIndex = 0;
    s.chicken.hopT = 0;
    s.chicken.hopDuration = 0;
    s.chicken.hopArc = 0;
    s.chicken.facing = 1;
    if (next < 1) {
      s.chicken.x = s.world.leftSceneW * 0.8 - 60;
      s.chicken.y = (s.world.roadTop + s.world.roadBottom) / 2;
    } else {
      s.chicken.x = laneCenterX(s, next - 1);
      s.chicken.y =
        (s.world.roadTop + s.world.roadBottom) / 2 + Math.sin(next * 1.7) * 12;
    }
    s.chicken.targetX = s.chicken.x;
    s.chicken.targetY = s.chicken.y;
    s.chicken.hopFromX = s.chicken.x;
    s.chicken.hopFromY = s.chicken.y;
    setPhase("playing");
    setLaneIndex(next);
    setMultiplier(shown);
    setHopBusy(false);
    hopInFlightRef.current = false;
  };

  const applyOpenRound = (data) => {
    if (!data || data.cat_mid == null) return false;
    const s = stateRef.current;
    if (!s) return false;
    roundRef.current = {
      cat_mid: data.cat_mid,
      multipliers: data.multipliers || null
    };
    setRoundId(String(data.cat_mid));
    if (Number(data.amount) > 0) {
      setBetAmount(Number(data.amount));
    }
    const lane = Number(data.currentLane) || 0;
    const fallbackMult = lane >= 1 ? LANE_MULTIPLIERS[lane - 1] || 1 : 1.0;
    placeChickenOnLane(s, lane, Number(data.multiplier) || fallbackMult);
    return true;
  };
  applyOpenRoundRef.current = applyOpenRound;

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/chicken/current-round")
      .then((res) => {
        if (cancelled) return;
        const data = res && res.data && res.data.data;
        if (!data || data.cat_mid == null) return;
        const waitForWorld = () => {
          if (cancelled) return;
          const s = stateRef.current;
          if (!s || !s.world || !s.world.width) {
            requestAnimationFrame(waitForWorld);
            return;
          }
          applyOpenRoundRef.current?.(data);
        };
        waitForWorld();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const requestHop = () => {
    const s = stateRef.current;
    debugger;
    if (!s || s.phase !== "playing") {
      hopInFlightRef.current = false;
      setHopBusy(false);
      return;
    }
    if (s.chicken.hopT < s.chicken.hopDuration) return;
    if (s.pendingLane != null || s.pendingCrash) return;
    if (hopInFlightRef.current) return;
    const round = roundRef.current;
    if (!round || !round.cat_mid) {
      hopInFlightRef.current = false;
      setHopBusy(false);
      return;
    }

    const next = s.laneIndex + 1;
    if (next > s.world.laneCount) {
      requestCashout();
      return;
    }

    const fromLane = s.laneIndex;
    const nextMult = LANE_MULTIPLIERS[next - 1] || 1;
    hopInFlightRef.current = true;
    setHopBusy(true);
    startHopToLane(s, next, nextMult);

    axiosInstance
      .post("/chicken/place-bet", { cat_mid: round.cat_mid, action: "hop" })
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        const result = data.result;
        if (result === "continue" || result === "crash") {
          if (
            Number.isFinite(Number(data.multiplier)) &&
            Number(data.multiplier) > 0
          ) {
            s.pendingMultiplier = Number(data.multiplier);
            if (hopHasLanded(s)) {
              s.multiplier = s.pendingMultiplier;
              s.pendingMultiplier = null;
              setMultiplier(s.multiplier);
            }
          }
          if (result === "crash") {
            s.pendingCrash = true;
            roundRef.current = null;
            refreshUserData();
            if (hopHasLanded(s) && s.phase === "playing") {
              spawnKillCarRef.current?.(s, Math.max(0, s.laneIndex - 1));
            }
          } else if (hopHasLanded(s)) {
            s.safeLaneIndex = s.laneIndex;
          }
          return;
        }
        if (result === "cashout") {
          roundRef.current = null;
          playCashoutRef.current?.(s, data.multiplier);
          refreshUserData();
        }
      })
      .catch((err) => {
        console.error("chicken/place-bet (hop) failed:", err);
        toast.error(
          err?.response?.data?.message || "Unable to hop. Please try again."
        );
        if (s.phase !== "playing" || s.pendingCrash) return;
        if (fromLane < 1) {
          snapToSidewalk(s);
        } else {
          startHopToLane(s, fromLane, LANE_MULTIPLIERS[fromLane - 1] || 1);
        }
      })
      .finally(() => {
        hopInFlightRef.current = false;
        clearHopBusyIfIdle(s);
      });
  };
  requestHopRef.current = requestHop;

  const playCashout = (s, paidMultiplier) => {
    if (!s || s.phase === "cashed" || s.phase === "crashed") return;
    const shown = Number(paidMultiplier);
    if (Number.isFinite(shown) && shown > 0) {
      s.multiplier = shown;
      setMultiplier(shown);
    }

    const winIdx = Math.max(0, s.laneIndex - 1);
    const { discY } = getDiscMetrics(s.world);
    const manholeX = laneCenterX(s, winIdx);
    const manholeY = discY - 8;

    s.phase = "cashed";
    s.cashFlash = 1;
    s.winLaneIndex = winIdx;
    s.winBarriersActive = true;
    s.winBarrierLaneIndex = s.laneIndex;
    s.pendingLane = null;
    s.pendingCrash = false;
    s.cars = [];
    s.chicken.x = manholeX;
    s.chicken.y = manholeY;
    s.chicken.targetX = manholeX;
    s.chicken.targetY = manholeY;
    s.chicken.hopFromX = manholeX;
    s.chicken.hopFromY = manholeY;
    s.chicken.hopT = 0;
    s.chicken.hopDuration = 0;
    s.chicken.hopArc = 0;
    s.chicken.facing = 1;

    for (let i = 0; i < 70; i++) {
      s.confetti.push({
        x: s.chicken.x,
        y: s.chicken.y,
        vx: (Math.random() - 0.5) * 320,
        vy: -Math.random() * 380 - 60,
        g: 540,
        life: 1.4 + Math.random() * 0.6,
        color: CAR_COLORS[i % CAR_COLORS.length],
        size: 4 + Math.random() * 4,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 6
      });
    }
    setPhase("cashed");
    setHopBusy(false);
    hopInFlightRef.current = false;
    clearCashResetTimer();
    cashResetRef.current = setTimeout(() => {
      cashResetRef.current = null;
      resetChickenToStart(stateRef.current);
    }, WIN_HOLD_MS);
  };
  playCashoutRef.current = playCashout;

  const requestCashout = () => {
    const s = stateRef.current;
    if (!s || s.phase !== "playing") return;
    if (s.pendingCrash || s.pendingLane != null) return;
    if (s.chicken.hopT < s.chicken.hopDuration) return;
    if (hopInFlightRef.current) return;
    if (s.laneIndex < 1) return;
    const round = roundRef.current;
    if (!round || !round.cat_mid) return;

    hopInFlightRef.current = true;
    setBetLoading(true);
    axiosInstance
      .post("/chicken/place-bet", { cat_mid: round.cat_mid, action: "cashout" })
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        if (data.result !== "cashout") {
          toast.error("Unable to cash out. Please try again.");
          return;
        }
        roundRef.current = null;
        playCashout(s, data.multiplier);
        refreshUserData();
      })
      .catch((err) => {
        console.error("chicken/place-bet (cashout) failed:", err);
        toast.error(
          err?.response?.data?.message ||
            "Unable to cash out. Please try again."
        );
      })
      .finally(() => {
        hopInFlightRef.current = false;
        setBetLoading(false);
      });
  };

  const triggerCrash = (s) => {
    if (s.phase === "crashed" || s.phase === "cashed") return;

    s.phase = "crashed";
    s.crashBarriersActive = false;
    s.crashBarrierLaneIndex = 0;
    s.crashFlash = 0;
    s.shake = 0;
    s.pendingCrash = false;
    for (let i = 0; i < 26; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 240;
      s.feathers.push({
        x: s.chicken.x,
        y: s.chicken.y,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp - 120,
        g: 380,
        life: 1.6,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 8,
        size: 6 + Math.random() * 5
      });
    }
    showCrashToast();
    setPhase("crashed");
    setHopBusy(false);
    hopInFlightRef.current = false;
    clearCrashResetTimer();
    crashResetRef.current = setTimeout(() => {
      crashResetRef.current = null;
      resetChickenToStart(stateRef.current);
    }, CRASH_RESET_MS);
  };

  /* ============== update tick ============== */

  const update = (s, dt) => {
    const c = s.chicken;

    // chicken hop animation
    if (c.hopDuration > 0 && c.hopT < c.hopDuration) {
      c.hopT = Math.min(c.hopDuration, c.hopT + dt);
      const u = c.hopT / c.hopDuration;
      const eu = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
      c.x = c.hopFromX + (c.targetX - c.hopFromX) * eu;
      c.y =
        c.hopFromY +
        (c.targetY - c.hopFromY) * eu -
        Math.sin(u * Math.PI) * c.hopArc;
      if (
        c.hopT >= c.hopDuration &&
        s.phase === "playing" &&
        s.pendingLane != null
      ) {
        s.laneIndex = s.pendingLane;
        s.pendingLane = null;
        const fallback =
          LANE_MULTIPLIERS[
            Math.max(0, Math.min(s.laneIndex - 1, LANE_MULTIPLIERS.length - 1))
          ] || 1;
        const m =
          Number.isFinite(s.pendingMultiplier) && s.pendingMultiplier > 0
            ? s.pendingMultiplier
            : fallback;
        s.pendingMultiplier = null;
        s.multiplier = m;
        setLaneIndex(s.laneIndex);
        setMultiplier(m);
        if (s.pendingCrash) {
          spawnCarOnLane(s, s.laneIndex - 1, true);
        } else if (!hopInFlightRef.current) {
          s.safeLaneIndex = s.laneIndex;
        }
        if (!hopInFlightRef.current) setHopBusy(false);
      }
    } else {
      c.walkT += dt * 6;
    }

    // Camera follow: scroll the full world (waiting area + road) so the chicken stays in view.
    const totalRoadW = s.world.laneWidth * s.world.laneCount;
    const worldWidth = s.world.leftSceneW + totalRoadW + s.world.sidewalkW;
    const maxOffset = Math.max(0, worldWidth - s.world.width);
    const desired = Math.max(0, c.x - s.world.width * 0.4);
    const targetOffset = Math.min(maxOffset, desired);
    s.world.offsetX += (targetOffset - s.world.offsetX) * Math.min(1, dt * 4);

    // ambient cars
    s.carSpawnTimer -= dt;
    if (s.carSpawnTimer <= 0 && s.phase !== "crashed" && s.phase !== "cashed") {
      s.carSpawnTimer = 0.12 + Math.random() * 0.22;
      // never spawn an ambient car on the chicken's current lane or the
      // lane it is hopping into: those lanes are reserved for the
      // scripted kill car (if any). When idle the chicken sits on
      // the sidewalk just to the left of lane 0, so keep lane 0 clear in
      // that phase too. When cashed/crashed the chicken is still
      // standing on its current manhole, so keep that lane clear.
      const blocked = reservedAmbientLanes(s);
      const candidates = [];
      for (let i = 0; i < s.world.laneCount; i++) {
        if (blocked.has(i) || hasLaneBarrier(s, i)) continue;
        candidates.push(i);
      }
      if (candidates.length) {
        // try a few candidates so the SAFE_GAP rejection doesn't
        // starve other lanes when the chosen lane is busy.
        for (let tries = 0; tries < 3; tries++) {
          const lane =
            candidates[Math.floor(Math.random() * candidates.length)];
          spawnCarOnLane(s, lane, false);
        }
      }
    }

    // move cars + collision
    for (const car of s.cars) {
      car.y += (car.vy || 0) * dt;
      if (s.phase === "playing") {
        const dx = c.x - car.x;
        const dy = c.y - car.y;
        if (
          Math.abs(dx) < car.w * 0.45 + 14 &&
          Math.abs(dy) < car.h * 0.45 + 14
        ) {
          if (car.lethal && s.pendingCrash) {
            triggerCrash(s);
          }
        }
      }
    }
    const blockedLanes = reservedAmbientLanes(s);
    s.cars = s.cars.filter((car) => {
      if (car.y <= s.world.roadTop - 240 || car.y >= s.world.roadBottom + 240)
        return false;
      const laneIdx = laneIdxFromCarX(s, car.x);
      if (!car.lethal && blockedLanes.has(laneIdx)) return false;
      if (car.ignoreBarrier) return true;
      return !hasLaneBarrier(s, laneIdx);
    });

    // particles
    for (const p of s.confetti) {
      p.vy += p.g * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      p.life -= dt;
    }
    s.confetti = s.confetti.filter((p) => p.life > 0);

    for (const f of s.feathers) {
      f.vy += f.g * dt;
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      f.rot += f.vr * dt;
      f.life -= dt;
    }
    s.feathers = s.feathers.filter((f) => f.life > 0);

    s.crashFlash = Math.max(0, s.crashFlash - dt * 1.6);
    s.cashFlash = Math.max(0, s.cashFlash - dt * 1.2);
  };

  const spawnCarOnLane = (s, laneIdx, fast) => {
    if (laneIdx < 0 || laneIdx >= s.world.laneCount) return;
    if (!fast && hasLaneBarrier(s, laneIdx)) return;
    if (!fast && reservedAmbientLanes(s).has(laneIdx)) return;
    const cx = laneCenterX(s, laneIdx);
    const w = 56,
      h = 96;
    const baseSpeed = fast ? 1620 : 660 + Math.random() * 660;
    const dir = 1;
    const spawnY = s.world.roadTop - 80;

    if (!fast) {
      const SAFE_GAP = h + 40;
      for (const other of s.cars) {
        if (Math.abs(other.x - cx) > 4) continue;
        const distToSpawn = Math.abs(other.y - spawnY);
        if (distToSpawn < SAFE_GAP) return;
        const ahead = (other.y - spawnY) * dir;
        if (ahead > -SAFE_GAP && ahead < SAFE_GAP * 3) return;
      }
    }

    s.cars.push({
      x: cx,
      y: spawnY,
      w,
      h,
      color: CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)],
      dir,
      vy: baseSpeed * dir,
      ignoreBarrier: !!fast,
      lethal: !!fast
    });
  };
  spawnKillCarRef.current = (s, laneIdx) => spawnCarOnLane(s, laneIdx, true);

  /* ============== draw ============== */

  const draw = (ctx, s, time) => {
    const {
      width: W,
      height: H,
      roadTop,
      roadBottom,
      sidewalkW,
      leftSceneW,
      laneWidth,
      laneCount,
      offsetX
    } = s.world;
    ctx.save();
    if (s.shake > 0) {
      ctx.translate(
        (Math.random() - 0.5) * s.shake,
        (Math.random() - 0.5) * s.shake
      );
    }

    // ===== full background: top‑down asphalt =====
    ctx.fillStyle = "#3a3a40";
    ctx.fillRect(0, 0, W, H);

    // world layer (scrolled)
    ctx.save();
    ctx.translate(-offsetX, 0);

    // ===== left waiting area / scenery =====
    if (
      s.leftSceneImg &&
      s.leftSceneImg.complete &&
      s.leftSceneImg.naturalWidth > 0
    ) {
      const img = s.leftSceneImg;
      ctx.drawImage(img, 0, 0, leftSceneW, H);
    } else {
      drawBrickSidewalk(ctx, 0, 0, leftSceneW, H);
      drawHedge(ctx, 4, 4, leftSceneW - 8, 120);
      drawHedge(ctx, 4, H - 140, leftSceneW - 8, 130);
      drawStreetLamp(ctx, leftSceneW - 18, H * 0.28);
    }

    // ===== road surface (uniform medium grey asphalt) =====
    const roadX = leftSceneW;
    const roadW = laneWidth * laneCount;
    ctx.fillStyle = "#6f6a66";
    ctx.fillRect(roadX, 0, roadW, H);

    // dashed lane separators (white)
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 12]);
    for (let i = 1; i < laneCount; i++) {
      const x = roadX + i * laneWidth;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // ===== manhole-cover style multiplier discs (one per lane center) =====
    const { discY, discR } = getDiscMetrics(s.world);
    for (let i = 0; i < laneCount; i++) {
      const cx = roadX + (i + 0.5) * laneWidth;
      const m = LANE_MULTIPLIERS[i] ?? 1;
      const won = isWinShowing(s) && s.winLaneIndex === i;
      const reached =
        (s.phase === "playing" && i < s.laneIndex) ||
        (s.crashBarriersActive && i < s.crashBarrierLaneIndex) ||
        (s.winBarriersActive && i < s.winBarrierLaneIndex) ||
        won;
      const current =
        !won &&
        s.laneIndex - 1 === i &&
        (s.phase === "playing" ||
          s.phase === "crashed" ||
          s.crashBarriersActive);
      drawManhole(
        ctx,
        cx,
        discY,
        discR,
        m,
        { reached, current, won },
        s.manholeImg,
        s.manholeReachedImg
      );
    }

    // ===== lane barriers (appear once the chicken crosses a manhole) =====
    const { centerY: barrierCenterY, barrierW, barrierH } = getBarrierLayout(s);
    for (let i = 0; i < laneCount; i++) {
      if (!hasLaneBarrier(s, i)) continue;
      const cx = roadX + (i + 0.5) * laneWidth;
      drawBarrier(ctx, cx, barrierCenterY, barrierW, barrierH, s.barrierImg);
    }

    // ===== right sidewalk: light brick + hedge =====
    const roadEndX = roadX + roadW;
    drawBrickSidewalk(ctx, roadEndX, 0, sidewalkW, H);
    drawHedge(ctx, roadEndX + 4, 4, sidewalkW - 8, 110);
    drawHedge(ctx, roadEndX + 4, H - 130, sidewalkW - 8, 120);

    // cars
    for (const car of s.cars) drawCar(ctx, car);

    drawChicken(ctx, s.chicken, s.phase, time, s.chickenImg);

    // Score bubble under the manhole the chicken is standing on (or win lane)
    if (isWinShowing(s) && s.winLaneIndex >= 0) {
      const winCx = roadX + (s.winLaneIndex + 0.5) * laneWidth;
      const winMult = s.multiplier || LANE_MULTIPLIERS[s.winLaneIndex] || 1;
      drawManholeRateTooltip(ctx, winCx, discY, discR, winMult, time);
    } else if (
      s.laneIndex > 0 &&
      (s.phase === "playing" || s.phase === "crashed" || s.crashBarriersActive)
    ) {
      const curIdx = s.laneIndex - 1;
      const curCx = roadX + (curIdx + 0.5) * laneWidth;
      const curMult = s.multiplier || LANE_MULTIPLIERS[curIdx] || 1;
      drawManholeRateTooltip(ctx, curCx, discY, discR, curMult, time);
    }

    // particles in world space
    for (const p of s.confetti) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      ctx.restore();
    }
    for (const f of s.feathers) {
      ctx.save();
      ctx.translate(f.x, f.y);
      ctx.rotate(f.rot);
      ctx.globalAlpha = Math.max(0, Math.min(1, f.life / 1.6));
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.ellipse(0, 0, f.size, f.size * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,0.2)";
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore(); // end world translate

    if (s.cashFlash > 0) {
      ctx.fillStyle = `rgba(60,200,80,${s.cashFlash * 0.22})`;
      ctx.fillRect(0, 0, W, H);
    }

    ctx.restore();
  };

  /* ============== UI handlers ============== */

  const onPlayClick = () => {
    const s = stateRef.current;
    if (!s) return;
    if (s.phase === "idle" || s.phase === "cashed" || s.phase === "crashed") {
      // First click of a fresh round → call backend to register the bet
      // and receive cat_mid + winning rate, then start the game.
      if (betLoading) return;
      clearCrashResetTimer();
      clearCashResetTimer();
      // If we're still in the post-round hold (cashed/crashed), drop
      // straight back to idle so startGame() will run cleanly.
      if (s.phase === "crashed" || s.phase === "cashed") {
        resetChickenToStart(s);
      } else {
        s.phase = "idle";
        setPhase("idle");
      }
      setBetLoading(true);
      axiosInstance
        .post("/chicken/place-bet", { amount: Number(betAmount) || 0 })
        .then((res) => {
          const data = (res && res.data && res.data.data) || {};
          if (!data.cat_mid) {
            console.error("chicken/place-bet: missing cat_mid", res?.data);
            setBetLoading(false);
            return;
          }

          roundRef.current = {
            cat_mid: data.cat_mid,
            multipliers: data.multipliers || null
          };
          setRoundId(String(data.cat_mid));
          // Pull fresh balance + exposure into the redux store so
          // the B / L numbers in the header update immediately.
          refreshUserData();
          setBetLoading(false);
          if (data.resumed) {
            applyOpenRound(data);
          } else {
            debugger;
            startGame();
          }
        })
        .catch((err) => {
          console.error("chicken/place-bet failed:", err);
          toast.error(
            err?.response?.data?.message ||
              "Unable to place bet. Please try again."
          );
          setBetLoading(false);
        });
    } else if (s.phase === "playing") {
      if (s.chicken.hopT >= s.chicken.hopDuration) requestHop();
    }
  };

  const onCashoutClick = () => requestCashout();

  const isPlaying = phase === "playing";

  return (
    <>
      <div className="aviator-scope player-scope aviator-bg-black">
        <ClientLayoutWithOutHeader showLoader={false}>
          <div className="details-tabs-wrapper1">
            <div className="main-page">
              <div className="row no-gutters">
                <div className="col-md-12">
                  <div className="bodyheight">
                    <div className="wrapper">
                      <TPStudioSidebar open={open} />
                    </div>

                    <div className="lobby-ctn BPGmaindiv">
                      <TpStudioHeader setOpen={setOpen} open={open} />
                      <TpStudioLoader displayLoader={displayLoader} />
                    </div>

                    <div
                      id="CR2-canvas-container"
                      ref={containerRef}
                      style={{ width: "100%", position: "relative" }}
                    >
                      <canvas id="CR2Canvas" ref={canvasRef} />
                      {phase === "cashed" && (
                        <div
                          className="cr-win-hud cr-win-hud-pulse"
                          aria-live="polite"
                        >
                          <span className="cr-win-hud-pill">WIN!</span>
                          <span className="cr-win-hud-mult">
                            {multiplier.toFixed(2)}X
                          </span>
                        </div>
                      )}
                    </div>

                    <div>
                      <div
                        className="mobile-play-wrapper"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "20px",
                          width: "100%",
                          marginTop: "20px"
                        }}
                      >
                        <div
                          className="mobile-buttons-row"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "20px",
                            width: isPlaying ? "100%" : "50%",
                            marginLeft: isPlaying ? "0%" : "25%"
                          }}
                        >
                          <button
                            className="game-btn cash-btn"
                            style={{
                              display: isPlaying ? "flex" : "none",
                              opacity: 1
                            }}
                            onClick={onCashoutClick}
                            disabled={betLoading || hopBusy || laneIndex < 1}
                          >
                            CASHOUT {multiplier.toFixed(2)}x
                          </button>
                          <button
                            className={
                              "game-btn main-btn" +
                              (betLoading || (isPlaying && hopBusy)
                                ? " main-btn-loading"
                                : phase === "idle"
                                ? " main-btn-pulse"
                                : "")
                            }
                            style={{ opacity: 1 }}
                            onClick={onPlayClick}
                            disabled={betLoading || (isPlaying && hopBusy)}
                          >
                            {betLoading || (isPlaying && hopBusy)
                              ? "WAIT..."
                              : phase === "crashed"
                              ? "TRY AGAIN"
                              : phase === "cashed"
                              ? "WON!"
                              : phase === "playing"
                              ? "GO"
                              : "PLAY"}
                          </button>
                        </div>

                        <div id="CR2-bet-panel" className="bet-panel">
                          <div className="full-bet-pannel">
                            <div className="bet-panel-row bet-panel-top">
                              <span
                                className="id-badge-label"
                                style={{ color: "white" }}
                              >
                                ID
                              </span>
                              <span
                                className="id-badge-sep"
                                style={{ color: "white" }}
                              >
                                –
                              </span>
                              <input
                                id="defaultBetSize"
                                type="number"
                                min="1"
                                step="1"
                                className="bet-input"
                                value={betAmount}
                                disabled={isPlaying || betLoading}
                                onChange={(e) =>
                                  setBetAmount(Number(e.target.value) || 0)
                                }
                              />
                            </div>
                            <div className="bet-panel-row bet-panel-chips">
                              {[2000, 5000, 10000, 25000].map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  className={
                                    "chip-btn" +
                                    (Number(betAmount) === v
                                      ? " chip-btn-active"
                                      : "")
                                  }
                                  data-bet={v}
                                  disabled={isPlaying || betLoading}
                                  onClick={() => setBetAmount(v)}
                                >
                                  <span className="CR2-coin">$</span>
                                  <span className="CR2-bet-text">{v}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ClientLayoutWithOutHeader>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={2800}
        hideProgressBar={false}
        closeButton={false}
        newestOnTop
        limit={1}
      />
      <style>
        {`
                    .cr-crash-toast {
                        background: #7a1f1f !important;
                        border-radius: 14px !important;
                        padding: 14px 22px !important;
                        min-height: unset !important;
                        box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45) !important;
                        overflow: hidden;
                    }
                    .cr-crash-toast .Toastify__toast-body {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .cr-crash-toast .Toastify__progress-bar {
                        background: rgba(0, 0, 0, 0.28) !important;
                        height: 4px;
                    }
                    .cr-crash-toast-body {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                        color: #ffffff;
                        font-size: 20px;
                        font-weight: 700;
                        line-height: 1.2;
                        white-space: nowrap;
                    }
                    .cr-crash-toast-icon {
                        flex-shrink: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 38px;
                        height: 38px;
                    }
                    .cr-crash-toast-icon-svg {
                        display: block;
                        width: 34px;
                        height: 38px;
                    }
                    .cr-crash-toast-text {
                        padding-top: 2px;
                    }
                    #CR2-canvas-container { background: #0d0d12; border-radius: 0; overflow: hidden; }
                    #CR2Canvas { display: block; width: 100%; }
                    .cr-round-id {
                        position: absolute;
                        top: 12px;
                        left: 12px;
                        z-index: 4;
                        color: #ffffff;
                        font-family: Arial, sans-serif;
                        font-size: 13px;
                        font-weight: 700;
                        letter-spacing: 0.02em;
                        background: rgba(0, 0, 0, 0.5);
                        border: 1px solid rgba(255, 255, 255, 0.12);
                        border-radius: 8px;
                        padding: 6px 10px;
                        pointer-events: none;
                        white-space: nowrap;
                    }
                    .cr-win-hud {
                        position: absolute;
                        top: 22px;
                        left: 50%;
                        transform: translateX(-50%);
                        z-index: 4;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        pointer-events: none;
                        min-width: 216px;
                        padding: 24px 38px 16px;
                        border-radius: 18px;
                        background: #1a4d2e;
                        border: 3.5px solid #8fdc3f;
                        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
                    }
                    .cr-win-hud-pill {
                        position: absolute;
                        top: -16px;
                        left: 50%;
                        transform: translateX(-50%);
                        padding: 6px 22px;
                        border-radius: 999px;
                        background: #ffeb3b;
                        border: 2.5px solid #1a4d2e;
                        color: #2a1f14;
                        font-family: 'Arial Black', Arial, sans-serif;
                        font-size: 18px;
                        font-weight: 800;
                        letter-spacing: 0.04em;
                        line-height: 1.2;
                        white-space: nowrap;
                    }
                    .cr-win-hud-mult {
                        color: #ffffff;
                        font-family: 'Arial Black', Arial, sans-serif;
                        font-size: 44px;
                        font-weight: 800;
                        line-height: 1.1;
                        letter-spacing: 0.02em;
                    }
                    .cr-win-hud-pulse {
                        animation: cr-win-pulse 1.4s ease-in-out infinite;
                    }
                    @keyframes cr-win-pulse {
                        0%, 100% { transform: translateX(-50%) scale(1); }
                        50%      { transform: translateX(-50%) scale(1.06); }
                    }
                    .main-btn {
                        background: linear-gradient(180deg, #ffeb3b 0%, #fbc02d 100%);
                        color: #5d4037;
                        width: 30%;
                    }
                    .main-btn-loading {
                        background: linear-gradient(180deg, #d9c82e 0%, #c49f1a 100%);
                        color: #4a3828;
                        cursor: wait;
                        opacity: 1;
                    }
                    .main-btn-pulse {
                        animation: cr-pulse 1.6s ease-in-out infinite;
                    }
                    @keyframes cr-pulse {
                        0%, 100% { transform: scale(1); box-shadow: 0 6px #00000044, 0 0 0 0 rgba(255,235,59,0.55); }
                        50%      { transform: scale(1.04); box-shadow: 0 6px #00000044, 0 0 0 12px rgba(255,235,59,0); }
                    }
                    .cash-btn {
                        background: linear-gradient(180deg, #66e07a 0%, #2ea54a 100%);
                        color: #0d2a12;
                        width: 30%;
                    }
                    .full-bet-panel{
                        background-color: #5E5E5E;
                    }
                    .game-btn {
                        border: none;
                        border-radius: 12px;
                        padding: 14px 6px;
                        font-size: 20px;
                        font-weight: 800;
                        text-transform: uppercase;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        box-shadow: 0 6px #00000044;
                        font-family: 'Arial Black', sans-serif;
                        outline: none;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .chip-btn{
                        height: 44px;
                        min-width: 72px; 
                        padding: 0 18px 0 12px;
                        border-radius: 10px; 
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        background: #1a1a1f;
                        color: rgb(255, 255, 255);
                        font-weight: 800;
                        cursor: pointer;
                        opacity: 1;
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        transition: border-color 0.15s ease, background 0.15s ease, transform 0.05s ease;
                    }
                    .chip-btn:hover{ border-color: rgba(255,255,255,0.18); }
                    .chip-btn:active{ transform: translateY(1px); }
                    .chip-btn-active{
                        border-color: #f5c518;
                        background: #20201a;
                    }
                    .chip-btn .CR2-coin{
                        width: 22px; height: 22px;
                        border-radius: 50%;
                        background: linear-gradient(180deg, #ffd84d 0%, #f5b800 100%);
                        color: #5d4037;
                        font-weight: 900;
                        font-size: 13px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 0 0 2px rgba(0,0,0,0.35) inset;
                    }
                    .chip-btn .CR2-bet-text{
                        font-size: 15px;
                        letter-spacing: 0.3px;
                    }
                    .full-bet-pannel{
                        width: 100%;
                        max-width: 500px;
                    }
                    /* ===== Desktop layout: PLAY button on the left, bordered bet panel on the right ===== */
                    @media (min-width: 769px){
                        .mobile-play-wrapper{
                            justify-content: space-between;
                            gap: 24px !important;
                        }
                        .mobile-buttons-row{
                            flex: 1 1 0;
                            width: auto !important;
                            gap: 16px !important;
                            justify-content: center !important;
                            padding-left: 0;
                        }
                        .mobile-buttons-row .game-btn{
                            min-width: 280px;
                            height: 64px;
                            padding: 14px 28px;
                            font-size: 22px;
                            letter-spacing: 1px;
                        }
                        .mobile-buttons-row .main-btn,
                        .mobile-buttons-row .cash-btn{
                            width: auto !important;
                        }
                        #CR2-bet-panel{
                            flex: 1 1 auto;
                            display: flex;
                            justify-content: flex-end;
                        }
                        #CR2-bet-panel .full-bet-pannel{
                            width: 100%;
                            max-width: 900px;
                            background: #1a1a1f;
                            border: 1px solid rgba(255,255,255,0.08);
                            border-radius: 14px;
                            padding: 14px 16px;
                            box-sizing: border-box;
                            display: flex;
                            flex-direction: column;
                            gap: 10px;
                        }
                        #CR2-bet-panel .bet-panel-top{
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 10px;
                        }
                        #CR2-bet-panel .bet-panel-chips{
                            display: flex;
                            flex-wrap: nowrap;
                            justify-content: space-between;
                            gap: 10px;
                        }
                        #CR2-bet-panel .bet-panel-chips .chip-btn{
                            flex: 1 1 0;
                            min-width: 0;
                            justify-content: center;
                        }
                    }
                    .id-badge-label{
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        min-width: 36px;
                        height: 32px;
                        padding: 0 10px;
                        border-radius: 8px;
                        background: #2a2a31;
                        font-size: 12px;
                        font-weight: 700;
                        white-space: nowrap;
                    }
                    .bet-input{
                        width: 130px; 
                        height: 44px;
                        border-radius: 10px;
                        border: 1px solid rgba(255, 255, 255, 0.08);
                        background: #111114;
                        color: rgb(255, 255, 255);
                        font-size: 18px; 
                        font-weight: 800; 
                        text-align: center;
                        outline: none;
                    }
                    @media (max-width: 768px){
                        .mobile-play-wrapper{
                            flex-direction: column !important;
                            align-items: stretch !important;
                            gap: 12px !important;
                            padding: 0 8px;
                            box-sizing: border-box;
                        }
                        .mobile-buttons-row{
                            order: 1;
                            width: 100%;
                            gap: 10px !important;
                        }
                        #CR2-bet-panel{
                            order: 2;
                            width: 100% !important;
                            background: #1a1a1a;
                            border-radius: 12px;
                            padding: 10px;
                            box-sizing: border-box;
                        }
                        .full-bet-pannel{
                            width: 100% !important;
                            max-width: 100% !important;
                        }
                        .game-btn{
                            flex: 1;
                            font-size: 16px;
                            padding: 15px 4px;
                        }
                        .main-btn,
                        .cash-btn{
                            width: auto !important;
                        }
                        .bet-panel-top{
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 6px;
                            margin-bottom: 8px;
                        }
                        .bet-input{
                            width: 100%;
                            max-width: 160px;
                        }
                        .bet-panel-chips{
                            display: flex;
                            flex-wrap: nowrap;
                            justify-content: space-between;
                            gap: 6px;
                        }
                        .chip-btn{
                            flex: 1;
                            min-width: 0;
                            padding: 0 6px;
                            font-size: 12px;
                            height: 38px;
                            gap: 4px;
                        }
                        .chip-btn .CR2-coin{
                            width: 18px;
                            height: 18px;
                            font-size: 11px;
                        }
                        .chip-btn .CR2-bet-text{
                            font-size: 12px;
                        }
                        .cr-round-id {
                            top: 8px;
                            left: 8px;
                            font-size: 12px;
                            padding: 5px 8px;
                        }
                    }
                    @media (max-width: 380px){
                        .game-btn{
                            font-size: 14px;
                            padding: 10px 2px;
                        }
                        .chip-btn{
                            font-size: 11px;
                            height: 34px;
                        }
                    }
                `}
      </style>
    </>
  );
}

/* ================= drawing helpers ================= */

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCar(ctx, car) {
  const { x, y, w, h, color, dir } = car;
  ctx.save();
  ctx.translate(x, y);
  if (dir < 0) ctx.scale(1, -1);

  // ground shadow
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  roundRect(ctx, -w / 2 + 4, -h / 2 + 6, w, h, 14);
  ctx.fill();

  // outer body silhouette
  const grad = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
  grad.addColorStop(0, shade(color, -0.15));
  grad.addColorStop(0.5, color);
  grad.addColorStop(1, shade(color, -0.35));
  ctx.fillStyle = grad;
  roundRect(ctx, -w / 2, -h / 2, w, h, 14);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // hood crease shading
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  roundRect(ctx, -w / 2 + 4, -h / 2 + 4, w - 8, h * 0.2, 8);
  ctx.fill();
  roundRect(ctx, -w / 2 + 4, h / 2 - h * 0.24, w - 8, h * 0.2, 8);
  ctx.fill();

  // windshield (front, top because car points up after scale)
  const wsGrad = ctx.createLinearGradient(0, -h * 0.28, 0, -h * 0.05);
  wsGrad.addColorStop(0, "rgba(40,60,80,0.95)");
  wsGrad.addColorStop(1, "rgba(160,210,240,0.85)");
  ctx.fillStyle = wsGrad;
  roundRect(ctx, -w / 2 + 6, -h * 0.3, w - 12, h * 0.24, 6);
  ctx.fill();

  // roof panel
  ctx.fillStyle = shade(color, -0.45);
  roundRect(ctx, -w / 2 + 8, -h * 0.05, w - 16, h * 0.22, 5);
  ctx.fill();
  // roof highlight
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  roundRect(ctx, -w / 2 + 10, -h * 0.04, (w - 20) * 0.5, h * 0.18, 3);
  ctx.fill();

  // rear window
  const rwGrad = ctx.createLinearGradient(0, h * 0.18, 0, h * 0.36);
  rwGrad.addColorStop(0, "rgba(160,210,240,0.85)");
  rwGrad.addColorStop(1, "rgba(40,60,80,0.95)");
  ctx.fillStyle = rwGrad;
  roundRect(ctx, -w / 2 + 8, h * 0.18, w - 16, h * 0.2, 6);
  ctx.fill();

  // side mirrors
  ctx.fillStyle = shade(color, -0.4);
  roundRect(ctx, -w / 2 - 4, -h * 0.18, 6, 6, 2);
  ctx.fill();
  roundRect(ctx, w / 2 - 2, -h * 0.18, 6, 6, 2);
  ctx.fill();

  // headlights (front)
  ctx.fillStyle = "#fff8c6";
  roundRect(ctx, -w / 2 + 5, -h / 2 + 3, 12, 5, 2);
  ctx.fill();
  roundRect(ctx, w / 2 - 17, -h / 2 + 3, 12, 5, 2);
  ctx.fill();

  // tail lights (rear)
  ctx.fillStyle = "#ff3b3b";
  roundRect(ctx, -w / 2 + 5, h / 2 - 8, 12, 5, 2);
  ctx.fill();
  roundRect(ctx, w / 2 - 17, h / 2 - 8, 12, 5, 2);
  ctx.fill();

  // center stripe (subtle)
  ctx.fillStyle = "rgba(255,255,255,0.10)";
  ctx.fillRect(-1, -h / 2 + 4, 2, h - 8);

  ctx.restore();
}

function drawChicken(ctx, c, phase, time, img) {
  const bob =
    phase === "playing" ? Math.sin(c.walkT) * 1.6 : Math.sin(time * 3) * 1.2;
  const dead = phase === "crashed";
  const onRoad = phase === "playing" || phase === "cashed";

  // If sprite is loaded AND chicken is alive, use sprite; otherwise (dead) fall through
  // to the procedural drawing which has the X-eyes / dead-chicken art.
  if (!dead && img && img.complete && img.naturalWidth > 0) {
    // waiting/idle chicken on the sidewalk = 3x (-15%); jumping chicken on the road = 2x.
    // Shrink on small screens so the chicken doesn't dominate the lane.
    const isMobile = typeof window !== "undefined" && window.innerWidth < 600;
    const TARGET_H = isMobile ? (onRoad ? 135 : 171) : onRoad ? 180 : 230;
    const aspect = img.naturalWidth / img.naturalHeight;
    const w = TARGET_H * aspect;
    const h = TARGET_H;
    ctx.save();
    ctx.translate(c.x, c.y + bob);
    if (c.facing < 0) ctx.scale(-1, 1);
    // ground shadow
    ctx.fillStyle = "rgba(0,0,0,0.30)";
    ctx.beginPath();
    ctx.ellipse(0, h * 0.42, w * 0.22, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    // sprite art is biased ~7px to the right of its bbox center; correct it
    // so the chicken visually sits on the manhole center while jumping.
    const spriteOffsetX = onRoad ? -7 : 0;
    ctx.drawImage(img, -w / 2 + spriteOffsetX, -h / 2, w, h);
    ctx.restore();
    return;
  }

  const SCALE = 1.7;
  ctx.save();
  ctx.translate(c.x, c.y + bob);
  ctx.scale(SCALE, SCALE);
  if (c.facing < 0) ctx.scale(-1, 1);
  if (dead) ctx.rotate(Math.PI / 2);

  const WHITE = "#ffffff";
  const OUTLINE = "rgba(0,0,0,0.18)";
  const FOOT = "#f5a623";
  const COMB = "#e23b3b";
  const BEAK = "#ffb52b";
  const BEAK_DARK = "#e89414";
  const TONGUE = "#e23b3b";

  // soft ground shadow
  ctx.fillStyle = "rgba(0,0,0,0.30)";
  ctx.beginPath();
  ctx.ellipse(0, 19, 18, 4.5, 0, 0, Math.PI * 2);
  ctx.fill();

  // feet (small orange triangles peeking from under body)
  const legSwing = phase === "playing" ? Math.sin(c.walkT) * 1.8 : 0;
  ctx.fillStyle = FOOT;
  // left foot
  ctx.beginPath();
  ctx.moveTo(-6 + legSwing, 16);
  ctx.lineTo(-2 + legSwing, 16);
  ctx.lineTo(-4 + legSwing, 20);
  ctx.closePath();
  ctx.fill();
  // right foot
  ctx.beginPath();
  ctx.moveTo(2 - legSwing, 16);
  ctx.lineTo(6 - legSwing, 16);
  ctx.lineTo(4 - legSwing, 20);
  ctx.closePath();
  ctx.fill();

  // body - big round white blob (slightly squished, fluffy bottom)
  ctx.fillStyle = WHITE;
  ctx.beginPath();
  ctx.ellipse(0, 4, 16, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  // fluffy bottom bumps
  ctx.beginPath();
  ctx.arc(-10, 14, 5, 0, Math.PI * 2);
  ctx.arc(-2, 16, 5.5, 0, Math.PI * 2);
  ctx.arc(7, 15, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.ellipse(0, 4, 16, 14, 0, 0, Math.PI * 2);
  ctx.stroke();

  // small wing tuft on side
  ctx.fillStyle = WHITE;
  ctx.beginPath();
  ctx.ellipse(-2, 6, 7, 5, -0.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = OUTLINE;
  ctx.stroke();

  // head - big round, sits on top of body
  ctx.fillStyle = WHITE;
  ctx.beginPath();
  ctx.arc(0, -7, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // comb - 3 red bumps on top
  ctx.fillStyle = COMB;
  ctx.beginPath();
  ctx.arc(-5, -16, 3, 0, Math.PI * 2);
  ctx.arc(0, -18, 3.4, 0, Math.PI * 2);
  ctx.arc(5, -16, 3, 0, Math.PI * 2);
  ctx.fill();

  // wattle (red dangly bit under beak)
  ctx.fillStyle = COMB;
  ctx.beginPath();
  ctx.ellipse(8, -2, 3, 4, 0.3, 0, Math.PI * 2);
  ctx.fill();

  // beak (orange, two-part open with tongue showing)
  // upper beak
  ctx.fillStyle = BEAK;
  ctx.beginPath();
  ctx.moveTo(8, -7);
  ctx.lineTo(16, -5);
  ctx.lineTo(9, -4);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = BEAK_DARK;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  // tongue between upper/lower
  ctx.fillStyle = TONGUE;
  ctx.beginPath();
  ctx.ellipse(11, -3, 2.2, 1.2, 0, 0, Math.PI * 2);
  ctx.fill();
  // lower beak
  ctx.fillStyle = BEAK;
  ctx.beginPath();
  ctx.moveTo(9, -3);
  ctx.lineTo(15, -2);
  ctx.lineTo(9, -1);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = BEAK_DARK;
  ctx.stroke();

  if (dead) {
    // X eyes
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-7, -10);
    ctx.lineTo(-2, -5);
    ctx.moveTo(-2, -10);
    ctx.lineTo(-7, -5);
    ctx.moveTo(0, -10);
    ctx.lineTo(5, -5);
    ctx.moveTo(5, -10);
    ctx.lineTo(0, -5);
    ctx.stroke();
  } else {
    // Big derpy googly eyes (pupils looking down-right for that derpy look)
    // eye whites
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(-4, -8, 4.2, 0, Math.PI * 2);
    ctx.arc(4, -8, 4.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 1;
    ctx.stroke();
    // big black pupils, offset down-right (derpy)
    ctx.fillStyle = "#1a1a1a";
    ctx.beginPath();
    ctx.arc(-3, -6.5, 2.6, 0, Math.PI * 2);
    ctx.arc(5, -6.5, 2.6, 0, Math.PI * 2);
    ctx.fill();
    // tiny white catchlights
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(-2.2, -7.3, 0.7, 0, Math.PI * 2);
    ctx.arc(5.8, -7.3, 0.7, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/* ===== environment helpers ===== */

function drawBrickSidewalk(ctx, x, y, w, h) {
  // light off-white base
  ctx.fillStyle = "#d8d6d0";
  ctx.fillRect(x, y, w, h);

  // brick rows (offset every other row)
  const bw = 30,
    bh = 16;
  ctx.strokeStyle = "rgba(120,118,110,0.55)";
  ctx.lineWidth = 1;
  for (let row = 0, ry = y; ry < y + h; ry += bh, row++) {
    const offset = (row % 2) * (bw / 2);
    // horizontal mortar line
    ctx.beginPath();
    ctx.moveTo(x, ry);
    ctx.lineTo(x + w, ry);
    ctx.stroke();
    // vertical mortar lines
    for (let bx = x - offset; bx < x + w; bx += bw) {
      if (bx > x && bx < x + w) {
        ctx.beginPath();
        ctx.moveTo(bx, ry);
        ctx.lineTo(bx, ry + bh);
        ctx.stroke();
      }
    }
  }
}

function drawHedge(ctx, x, y, w, h) {
  // dark outline / base
  ctx.fillStyle = "#2c6b34";
  const r = Math.min(w, h) * 0.45;
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();
  // mid green inner shape (slightly inset)
  ctx.fillStyle = "#5cb35a";
  const inset = 4;
  roundRect(
    ctx,
    x + inset,
    y + inset,
    w - inset * 2,
    h - inset * 2,
    Math.max(2, r - inset)
  );
  ctx.fill();
  // a few lighter highlight blobs
  ctx.fillStyle = "#7ed07a";
  for (let i = 0; i < 5; i++) {
    const cx = x + 12 + ((i * 53) % Math.max(1, w - 24));
    const cy = y + 10 + ((i * 37) % Math.max(1, h - 20));
    ctx.beginPath();
    ctx.arc(cx, cy, 5 + (i % 3) * 2, 0, Math.PI * 2);
    ctx.fill();
  }
  // dark outline
  ctx.strokeStyle = "#1f4f25";
  ctx.lineWidth = 2;
  roundRect(ctx, x, y, w, h, r);
  ctx.stroke();
}

function drawStreetLamp(ctx, x, y) {
  // top-down L-shape: vertical pole going up, horizontal arm reaching toward road, small head at end
  ctx.fillStyle = "#7a7a82";
  ctx.strokeStyle = "#3a3a42";
  ctx.lineWidth = 1.5;
  // base circle (where pole meets ground)
  ctx.beginPath();
  ctx.arc(x, y, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // vertical pole segment going up from base
  roundRect(ctx, x - 3, y - 30, 6, 30, 2);
  ctx.fill();
  ctx.stroke();
  // horizontal arm extending toward the road
  roundRect(ctx, x, y - 33, 38, 6, 2);
  ctx.fill();
  ctx.stroke();
  // lamp head at end of arm
  ctx.fillStyle = "#5a5a62";
  roundRect(ctx, x + 30, y - 40, 16, 18, 4);
  ctx.fill();
  ctx.stroke();
  // small bright lens
  ctx.fillStyle = "#ffe89a";
  ctx.beginPath();
  ctx.arc(x + 38, y - 31, 3, 0, Math.PI * 2);
  ctx.fill();
}

function drawBarrier(ctx, cx, cy, w, h, img) {
  if (img && img.complete && img.naturalWidth > 0) {
    ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
    return;
  }
  ctx.fillStyle = "#f5b800";
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h * 0.55, 4);
  ctx.fill();
  ctx.fillStyle = "#95989c";
  ctx.fillRect(cx - w / 2 + w * 0.12, cy - h / 2, w * 0.07, h);
  ctx.fillRect(cx + w / 2 - w * 0.19, cy - h / 2, w * 0.07, h);
}

function isWinShowing(s) {
  return (s.phase === "cashed" || s.cashFlash > 0.05) && s.winLaneIndex >= 0;
}

//below chicken tooltip
function drawManholeRateTooltip(ctx, cx, cy, r, multiplier, time) {
  const pulse = 1 + 0.025 * (1 + Math.sin(time * 4.5));
  const txt = multiplier.toFixed(2) + "x";
  const fontSize = Math.max(12, Math.round(r * 0.3));
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  const textW = ctx.measureText(txt).width;
  const padX = Math.max(10, r * 0.14);
  const boxW = textW + padX * 2;
  const boxH = Math.max(24, r * 0.4);
  const boxTop = cy + r * 0.34 + 30;
  const boxX = cx - boxW / 2;

  ctx.save();
  ctx.translate(cx, boxTop + boxH / 2);
  ctx.scale(pulse, pulse);
  ctx.translate(-cx, -(boxTop + boxH / 2));

  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;

  ctx.fillStyle = "#2F4F86";
  ctx.beginPath();
  ctx.moveTo(cx, boxTop - 7);
  ctx.lineTo(cx - 8, boxTop + 2);
  ctx.lineTo(cx + 8, boxTop + 2);
  ctx.closePath();
  ctx.fill();

  roundRect(ctx, boxX, boxTop, boxW, boxH, 7);
  ctx.fill();

  ctx.shadowColor = "transparent";
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(txt, cx, boxTop + boxH / 2);
  ctx.restore();
}

//chicken round circle and text
function drawManhole(ctx, cx, cy, r, multiplier, opt, img, reachedImg) {
  const { reached, current, won } = opt || {};
  const isReached = won || current || reached;
  const activeImg =
    isReached &&
    reachedImg &&
    reachedImg.complete &&
    reachedImg.naturalWidth > 0
      ? reachedImg
      : img;

  if (activeImg && activeImg.complete && activeImg.naturalWidth > 0) {
    ctx.drawImage(activeImg, cx - r, cy - r, r * 2, r * 2);
  } else {
    // fallback procedural disc
    ctx.fillStyle = "#3f3f44";
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    let body = "#6a6a70";
    if (won) body = "#c9a028";
    else if (current) body = "#a89030";
    else if (reached) body = "#4f8a52";
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(cx, cy, r - 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#2a2a2e";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  // multiplier text on disc (current/win use the bottom tooltip instead)
  if (won || current) return;

  const txt = multiplier.toFixed(2) + "x";
  const fontSize = Math.max(8, Math.round(r * 0.3)); // 25% smaller than previous r * 0.4
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.shadowColor = "rgba(0,0,0,0.75)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 2;
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  ctx.strokeText(txt, cx, cy);
  ctx.fillStyle = "#fff";
  ctx.fillText(txt, cx, cy);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

function shade(hex, pct) {
    const h = hex.replace("#", "");
    const num = parseInt(h, 16);
    let r = (num >> 16) & 0xff, g = (num >> 8) & 0xff, b = num & 0xff;
    r = Math.max(0, Math.min(255, Math.round(r + 255 * pct)));
    g = Math.max(0, Math.min(255, Math.round(g + 255 * pct)));
    b = Math.max(0, Math.min(255, Math.round(b + 255 * pct)));
    return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}
